import { ChevronBottomOutlineIcon, ChevronTopOutlineIcon } from '@ikyucom/design-system-icons-react'
import * as Popover from '@radix-ui/react-popover'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { ScopeProvider } from 'jotai-scope'
import { useCallback, useEffect } from 'react'
import type { PropsWithChildren, ReactNode } from 'react'
import { css, cva } from '~/styled-system/css'
import { FilterChip } from './FilterChip'

type Props<T> = {
  trigger: ReactNode
  color: 'default' | 'active' | 'disabled'
  data: T
}

export function FilterChipsWithPopover<T>({
  data,
  trigger,
  color,
  children,
}: PropsWithChildren<Props<T>>) {
  return (
    <Provider>
      <Body data={data} trigger={trigger} color={color}>
        {children}
      </Body>
    </Provider>
  )
}

const chevronStyle = cva({
  base: { color: 'gray.600' },
  variants: {
    active: {
      true: { color: 'button.tertiary.icon' },
    },
  },
})

function Body<T>({ data, trigger, color, children }: PropsWithChildren<Props<T>>) {
  const opened = useOpened()
  const toggle = useToggle()
  const close = useClose()
  const chevron = chevronStyle({ active: color === 'active' })
  return (
    <Popover.Root open={opened}>
      <Popover.Trigger onClick={toggle}>
        <div className="inline-flex">
          <FilterChip color={color}>
            <div className={css({ display: 'flex', alignItems: 'center', columnGap: '1' })}>
              {trigger}
              {opened && color !== 'disabled' ? (
                <ChevronTopOutlineIcon width={13} height={13} className={chevron} />
              ) : (
                <ChevronBottomOutlineIcon width={13} height={13} className={chevron} />
              )}
            </div>
          </FilterChip>
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="mx-3 mt-1 bg-white" onInteractOutside={close}>
          <DraftProvider data={data}>{children}</DraftProvider>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

const DEBUG_PREFIX = 'FilterChipsWithPopover'

const opened$ = atom(false)
opened$.debugLabel = DEBUG_PREFIX + 'opened$'

// popover の開閉状態を管理する Provider
function Provider({ children }: PropsWithChildren<{}>) {
  return <ScopeProvider atoms={[opened$]}>{children}</ScopeProvider>
}

const toggle$ = atom(null, (_, set) => set(opened$, (prev) => !prev))
const close$ = atom(null, (_, set) => set(opened$, false))

export function useOpened() {
  return useAtomValue(opened$)
}

export function useToggle() {
  return useSetAtom(toggle$)
}

export function useClose() {
  return useSetAtom(close$)
}

export function useCommit<T>(setData: (v: T) => void) {
  const close = useClose()
  const draft = useDraft<T>()
  return useCallback(() => {
    setData(draft)
    close()
  }, [close, draft, setData])
}

const draft$ = atom<any | undefined>(undefined)
draft$.debugLabel = DEBUG_PREFIX + 'draft$'

export function useDraft<T>() {
  return useAtomValue(draft$) as T
}

// https://github.com/pmndrs/jotai/blob/main/src/react/useSetAtom.ts#L9 を参考に作成
type SetAtom<Value, Result> = (fn: (v: Value) => Value) => Result

export function useSetDraft<T>() {
  return useSetAtom(draft$) as SetAtom<T, void>
}

// Popover.content 内で draft の初期化を行う Provider
// useCommit や useDraft で atom を操作する
function DraftProvider<T>({ data, children }: PropsWithChildren<{ data: T }>) {
  return (
    <ScopeProvider atoms={[draft$]}>
      <UseInit data={data} />
      {children}
    </ScopeProvider>
  )
}

function UseInit<T>({ data }: { data: T }) {
  const set = useSetAtom(draft$)
  useEffect(() => {
    set(data)
  }, [data, set])
  return null
}
